/* eslint-disable react/display-name */
import React, { useState, useContext, useCallback, useEffect } from 'react';

import * as yup from 'yup';

import { makeStyles } from '@material-ui/core/styles';
import { Fade, Grid, Typography, useMediaQuery } from '@material-ui/core';

import {
  UpdateContract,
  VerifyLastUserContractAPI
} from '../../../../API/Contracts/ContractsAPI';
import {
  UserContext,
  UserDispatchContext
} from '../../../../Contexts/UserContext';
import { ContractsRefreshContext } from '../../../../Contexts/ContractsContext';

import BaseButton from '../../../../Components/Buttons/BaseButton';
import BaseDialog from '../../../../Components/Dialogs/BaseDialog';
import { SlideUpTransition } from '../../../../Components/Transitions/Transitions';

import {
  redirectOnAuthFailure,
  extractErrorMessage,
  extractErrorCode
} from '../../../../Utils/Errors/Errors';
import RequestDialog from '../../../../Components/Dialogs/RequestDialog';
import PhoneVerificationDialog from '../../../Profile/PhoneVerificationDialog';
import { ResendPhoneConfirmationAPI } from '../../../../API/UserAPI';
import { ALERT_TYPE } from '../../../../Components/Alerts/alert_enums';
import ContractAssociationForm from '../../ContractAssociation/ContractAssociationForm/ContractAssociationForm';
import { history } from '../../../../Routes/history';
import { ROUTE_NAMES } from '../../../../Routes/Routes';
import { Company } from '../../../../Configs/general';
import ConfirmActionDialog from '../../../../Components/Dialogs/ConfirmActionDialog';
import FlatButton from '../../../../Components/Buttons/FlatButton';
import {
  DactivationReasonLabels,
  isValidDeactivationReason,
  isDigitalInvoiceAllowedByContractType
} from '../../contract_enums';
import { logoutUser } from '../../../../Utils/User/Actions';
import { DejalaIrContext } from '../../../../Contexts/DejalaIrContext';
import { openDigitalInvoiceThanksPage } from '../../../../Utils/Contract/openDigitalInvoiceThanksPage';
import GroupRadioForm from '../../../../Components/Radio/GroupRadioForm';

const editContractSchema = yup.object({
  aliasName: yup
    .string()
    .trim()
    .max(12, 'Debe tener 12 letras o menos.')
    .required('Ingresa un alias'),
  aliasIcon: yup.number().required('Selecciona un ícono'),
  referredCode: yup.string().when('$isReferredCodeRequired', {
    is: true,
    then: schema =>
      schema
        .trim()
        .matches(/^[a-zA-Z0-9]*$/, 'El campo debe ser alfanumérico')
        .max(50, 'Debe tener 50 caracteres o menos.')
        .required('Ingresa un código'),
    otherwise: schema => schema.trim().default('')
  })
});

const getPhoneActionText = (userPhone, userPhoneValidated) => {
  if (!userPhone) {
    return 'Agregar';
  }

  if (!userPhoneValidated) {
    return 'Confirmar';
  }

  return 'Validar';
};

const EditContractGDC = props => {
  // * CONTEXTS
  const currentUser = useContext(UserContext);
  const setCurrentUser = useContext(UserDispatchContext);
  const refreshContracts = useContext(ContractsRefreshContext);
  const { validateCampaign } = useContext(DejalaIrContext);
  const {
    contract,
    open,
    setDialog,
    setAlert,
    editFromParams,
    activateDigitalInvoice,
    digitalInvoiceSweetAlertOnclick
  } = props;

  const {
    id: contractId,
    sendInvoice: currentSendInvoice,
    digitalInvoice: currentDigitalInvoice,
    sendEmailInvoice: currentSendEmail,
    sendSmsInvoice: currentSendSms,
    sendWhatsAppInvoice: currentSendWhatsApp,
    alias: currentAlias,
    icon: currentIcon
  } = contract;

  const allowedContractType = isDigitalInvoiceAllowedByContractType(contract);

  // * STATE HOOKS
  const [aliasName, setAliasName] = useState(currentAlias);
  const [aliasIcon, setAliasIcon] = useState(currentIcon);
  const [sendInvoice, setSendInvoice] = useState(
    currentSendInvoice || activateDigitalInvoice
  );
  const [digitalInvoice, setDigitalInvoice] = useState(
    currentDigitalInvoice || activateDigitalInvoice
  );
  const [digitalChannels, setDigitalChannels] = useState({
    email: currentSendEmail || true,
    whatsapp: currentSendWhatsApp || false,
    sms: currentSendSms || false
  });
  const [canSubmit, setCanSubmit] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [needsPhoneValidation, setNeedsPhoneValidation] = useState(false);
  const [openPhoneValidationDialog, setOpenPhoneValidationDialog] = useState(
    false
  );
  const [
    openWhatsAppVerificationDialog,
    setOpenWhatsAppVerificationDialog
  ] = useState(false);
  const [openVerifyOnlyUserDialog, setOpenVerifyOnlyUserDialog] = useState(
    false
  );
  const [referredCode, setReferredCode] = useState('');
  const [isReferredCodeRequired, setIsReferredCodeRequired] = useState(false);
  const [
    digitalInvoiceDeactivationReason,
    setDigitalInvoiceDeactivationReason
  ] = useState();

  // * OTHER HOOKS
  const classes = useStyles();
  const smallScreen = useMediaQuery(theme =>
    theme.breakpoints.down(theme.breakpoints.values.sm)
  );

  const phoneActionText =
    currentUser &&
    getPhoneActionText(currentUser.phone, currentUser.phoneValidated);

  // validate contract schema
  useEffect(() => {
    try {
      editContractSchema.validateSync(
        { aliasName, aliasIcon, referredCode },
        { context: { isReferredCodeRequired } }
      );
      setErrors({});
      setCanSubmit(true);
    } catch (err) {
      setCanSubmit(false);
      setErrors({
        [err.path]: err.errors[0]
      });
    }
  }, [
    aliasName,
    aliasIcon,
    sendInvoice,
    digitalInvoice,
    referredCode,
    isReferredCodeRequired
  ]);

  const handleClose = useCallback(() => {
    if (editFromParams) {
      history.push(ROUTE_NAMES.contracts);
    }

    setDialog(false);
  }, [setDialog, editFromParams]);

  // * FUNCTIONS
  // Sends a WhatsApp-first OTP and opens the code dialog to certify the
  // phone for the WhatsApp digital-invoice channel
  const openWhatsAppRevalidation = useCallback(async () => {
    setLoading(true);
    const resendResponse = await ResendPhoneConfirmationAPI(currentUser.token, {
      whatsapp: true
    });

    if (!resendResponse.success) {
      setAlert({
        type: ALERT_TYPE.ERROR,
        message:
          extractErrorMessage(resendResponse).message ||
          'Error al enviar el código'
      });
      setLoading(false);
      return { unmounting: false, closeDialog: false };
    }

    setOpenWhatsAppVerificationDialog(true);
    setLoading(false);
    return { unmounting: false, closeDialog: false };
  }, [currentUser.token, setAlert, setOpenWhatsAppVerificationDialog]);

  const editContractAPI = useCallback(async () => {
    setLoading(true);

    // If the contract has an active digital invoice,
    // the user's response is taken as sendInvoice.
    // If it does not have it active, sendInvoice will
    // be the same as digitalInvoice.

    const realSendInvoice = currentDigitalInvoice
      ? sendInvoice
      : digitalInvoice;

    const realDigitalInvoice = currentDigitalInvoice || digitalInvoice;

    const digitalInvoiceSettings = {
      digitalInvoice: openVerifyOnlyUserDialog
        ? digitalInvoice
        : realDigitalInvoice,
      sendInvoice: realSendInvoice,
      sendEmailInvoice: realSendInvoice,
      sendWhatsAppInvoice: realSendInvoice && digitalChannels.whatsapp,
      sendSmsInvoice: realSendInvoice && digitalChannels.sms
    };

    // When the contract to associate is not an
    // allowed one for digital invoice, it sets
    // every digital invoice setting to false
    if (!allowedContractType) {
      for (const key of Object.keys(digitalInvoiceSettings)) {
        digitalInvoiceSettings[key] = false;
      }
    }

    const editedAssociation = {
      ...digitalInvoiceSettings,
      digitalInvoiceDeactivationReason,
      alias: aliasName.trim(),
      icon: aliasIcon,
      referredCode,
      origin: {
        url: window.location.pathname
      }
    };

    const response = await UpdateContract(
      currentUser.token,
      contractId,
      editedAssociation
    );

    setLoading(false);
    if (response.success) {
      await openDigitalInvoiceThanksPage({
        wasDigitalInvoiceActive: currentDigitalInvoice,
        shouldRedirect: digitalInvoice || realSendInvoice,
        contractId,
        validateCampaign
      });

      handleClose();

      refreshContracts();
      setAlert({
        type: 'success',
        message: `Se ha actualizado ${Company.contractConjugation.regular.singular.article} "${aliasName}"`
      });

      return { unmounting: true };
    }

    if (
      redirectOnAuthFailure(response, '/', () => logoutUser(setCurrentUser))
    ) {
      return { unmounting: true };
    }

    const errorMessageResponse = extractErrorMessage(response);

    const errorCode = extractErrorCode(response).code;
    if (errorCode && errorCode === 'WHATSAPP-PHONE-VALIDATION-REQUIRED') {
      return openWhatsAppRevalidation();
    }

    setAlert({
      type: 'error',
      message: errorMessageResponse.message
    });

    if (errorMessageResponse.key === 'inBillingPeriod') {
      return handleClose();
    }

    return { unmounting: false, closeDialog: true };
  }, [
    currentUser,
    contractId,
    aliasIcon,
    aliasName,
    refreshContracts,
    setAlert,
    setCurrentUser,
    digitalInvoice,
    digitalChannels,
    sendInvoice,
    currentDigitalInvoice,
    validateCampaign,
    referredCode,
    openVerifyOnlyUserDialog,
    allowedContractType,
    digitalInvoiceDeactivationReason,
    handleClose,
    setOpenPhoneValidationDialog,
    openWhatsAppRevalidation
  ]);

  const closePhoneDialog = useCallback(() => {
    setOpenPhoneValidationDialog(false);
  }, []);

  const closeVerifyOnlyUserDialog = useCallback(() => {
    setOpenVerifyOnlyUserDialog(false);
    setDigitalInvoiceDeactivationReason();
  }, []);

  const onChangeAliasName = useCallback(value => {
    setAliasName(value);
    setErrors(errs => ({ ...errs, aliasName: null }));
  }, []);

  const onSubmitEdit = useCallback(() => {
    if (needsPhoneValidation) {
      setOpenPhoneValidationDialog(true);
      return;
    }
    editContractAPI();
  }, [needsPhoneValidation, editContractAPI]);

  const onSubmit = useCallback(async () => {
    setLoading(true);

    const response = await VerifyLastUserContractAPI(
      currentUser.token,
      contractId,
      {
        userId: currentUser.id
      }
    );

    setLoading(false);

    if (!response.success) {
      setAlert({
        type: 'error',
        message: extractErrorMessage(response).message
      });
      return;
    }

    const { isLastUser } = response.data.data;
    if (needsPhoneValidation) {
      setOpenPhoneValidationDialog(true);
      return;
    }

    if (!sendInvoice && !digitalInvoice && isLastUser) {
      setOpenVerifyOnlyUserDialog(true);
    } else {
      editContractAPI();
    }
  }, [
    needsPhoneValidation,
    editContractAPI,
    contractId,
    currentUser.id,
    currentUser.token,
    digitalInvoice,
    sendInvoice,
    setAlert
  ]);

  // if i send acceptText to renderAction callback
  // is when is activated the modal to confirm
  // deactivate sendInvoice and deactivate digital invoice
  const renderActions = useCallback(
    (handleConfirmClick, acceptText) => {
      if (acceptText) {
        const validReason = isValidDeactivationReason(
          digitalInvoiceDeactivationReason
        );

        return () => (
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <Fade in={validReason}>
                <FlatButton
                  onClick={handleConfirmClick}
                  disabled={!canSubmit || loading || !validReason}
                >
                  Desactivar
                </FlatButton>
              </Fade>
            </Grid>
            <Grid item>
              <BaseButton onClick={closeVerifyOnlyUserDialog}>
                Mantener activas
              </BaseButton>
            </Grid>
          </Grid>
        );
      }
      return () => (
        <>
          <BaseButton
            onClick={handleClose}
            variant="outlined"
            color="primary"
            size="small"
          >
            Cancelar
          </BaseButton>
          <BaseButton
            onClick={handleConfirmClick}
            type="submit"
            form="editContractForm"
            color="primary"
            disabled={!canSubmit || loading}
            size="small"
            autoFocus
          >
            Guardar
          </BaseButton>
        </>
      );
    },
    [
      canSubmit,
      handleClose,
      loading,
      closeVerifyOnlyUserDialog,
      digitalInvoiceDeactivationReason
    ]
  );

  const renderContent = () => {
    return (
      <div className={classes.content}>
        <ContractAssociationForm
          dialogMode
          editMode
          currentUser={currentUser}
          contract={contract}
          sendInvoice={sendInvoice}
          setSendInvoice={setSendInvoice}
          isDigitalInvoice={digitalInvoice}
          setIsDigitalInvoice={setDigitalInvoice}
          digitalChannels={digitalChannels}
          setDigitalChannels={setDigitalChannels}
          aliasName={aliasName}
          setAliasName={onChangeAliasName}
          aliasIcon={aliasIcon}
          setAliasIcon={setAliasIcon}
          errors={errors}
          needsPhoneValidation={needsPhoneValidation}
          setNeedsPhoneValidation={setNeedsPhoneValidation}
          activateMode={activateDigitalInvoice}
          digitalInvoiceSweetAlertOnclick={digitalInvoiceSweetAlertOnclick}
          setCanSubmit={setCanSubmit}
          actions={renderActions(onSubmit)}
          referredCode={referredCode}
          setReferredCode={code => setReferredCode(code.toUpperCase())}
          isReferredCodeRequired={isReferredCodeRequired}
          setIsReferredCodeRequired={setIsReferredCodeRequired}
          referredCodeEnable
          allowedContractType={allowedContractType}
        />
      </div>
    );
  };
  const renderVerifyDialog = () => {
    return (
      <>
        <Typography paragraph className={classes.text}>
          {currentUser.firstName}, al desactivar las notificaciones del{' '}
          {Company.contractConjugation.regular.singular.main}{' '}
          <b>
            {aliasName} {contractId}
          </b>
          , el envío de tu factura pasará de factura digital a factura física.
          Esto ocurre porque eres el único usuario con notificaciones activas de
          factura digital. ¿Estás seguro que quieres desactivar las
          notificaciones de tu factura?
        </Typography>
        <div className={classes.reasonFormContainer}>
          <GroupRadioForm
            labels={DactivationReasonLabels}
            selectedReason={digitalInvoiceDeactivationReason}
            setSelectedReason={setDigitalInvoiceDeactivationReason}
            disable={loading}
          />
        </div>
      </>
    );
  };

  return (
    <>
      {openPhoneValidationDialog && (
        <RequestDialog
          open={openPhoneValidationDialog}
          setDialog={setOpenPhoneValidationDialog}
          beforeClosing={closePhoneDialog}
          requestTitle={`${phoneActionText} número celular`}
          requestCallback={editContractAPI}
          withoutObservation={true}
          backButton={{
            text: 'Cancelar',
            handle: closePhoneDialog
          }}
          submitButtonText="Continuar"
          requestNode={
            <Typography paragraph className={classes.text}>
              {`Para llevar a cabo esta acción es necesario ${phoneActionText.toLowerCase()} tu número de celular`}
            </Typography>
          }
          contentSize="small"
          otpChannels={{
            sms: digitalChannels.sms,
            whatsapp: digitalChannels.whatsapp
          }}
        />
      )}
      {openWhatsAppVerificationDialog && (
        <PhoneVerificationDialog
          open={openWhatsAppVerificationDialog}
          setDialog={setOpenWhatsAppVerificationDialog}
          setAlert={setAlert}
          title="Validar número celular"
          otpChannels={{ whatsapp: true }}
          successCallback={editContractAPI}
        />
      )}
      {openVerifyOnlyUserDialog && (
        <ConfirmActionDialog
          open={open}
          handleClose={closeVerifyOnlyUserDialog}
          title="Confirmación de desactivación de notificaciones"
          content={renderVerifyDialog}
          TransitionComponent={smallScreen ? SlideUpTransition : undefined}
          paperClass={classes.dialogPaper}
          actions={renderActions(onSubmitEdit, onSubmitEdit)}
          acceptText="Desactivar"
          actionsStyle={classes.contentDialogStyle}
          loading={loading}
        />
      )}
      <BaseDialog
        open={open}
        handleClose={handleClose}
        title={
          activateDigitalInvoice
            ? 'Activar Factura Digital'
            : `Editar ${Company.contractConjugation.regular.singular.main}`
        }
        actions={renderActions(onSubmit)}
        content={renderContent}
        fullScreen={smallScreen}
        TransitionComponent={smallScreen ? SlideUpTransition : undefined}
        paperClass={classes.dialogPaper}
        contentStyle={classes.contentStyle}
      />
    </>
  );
};

const useStyles = makeStyles(theme => ({
  text: {
    fontSize: 14,
    color: theme.palette.text.primary
  },
  content: {
    [theme.breakpoints.up('sm')]: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    }
  },
  contentStyle: {
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      padding: theme.spacing(3)
    }
  },
  contentDialogStyle: {
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      padding: theme.spacing(2)
    },
    justifyContent: 'space-between',
    padding: theme.spacing(2, 4)
  },
  alias: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center'
  },
  aliasIcon: {
    minWidth: 67
  },
  aliasText: {
    minWidth: 150,
    marginLeft: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      minWidth: 250
    }
  },
  switchContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    [theme.breakpoints.up(theme.breakpoints.values.sm)]: {
      flexDirection: 'row'
    }
  },
  switchText: {
    fontSize: 14,
    paddingRight: 12,
    [theme.breakpoints.up(theme.breakpoints.values.sm)]: {
      paddingRight: 12
    },
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      marginBottom: theme.spacing(3)
    }
  },
  dialogPaper: {
    maxWidth: 700
  },
  reasonFormContainer: {
    marginTop: theme.spacing(3)
  }
}));

export default EditContractGDC;
